import React, { lazy, useLayoutEffect } from 'react';

// MUI
import { CssBaseline, GlobalStyles, ThemeProvider } from '@mui/material';

// Third party
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

// Non Lazy Routes
import Login from './pages/Shared/Login';
import Register from './pages/Shared/Register';
import NotFoundPage from './pages/Shared/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import UnauthorizedPage from './pages/Shared/UnauthorizedPage';
import ResetPasswordPage from './pages/Shared/ResetPasswordPage';

// Components
import theme from './theme/theme';
import { ESalesAccountType, ESuperUserAccountType, Role, UserType } from './constants';

// Lazy Index Pages
const ShooterHomePage = lazy(() => import('./pages/Shooter'));
const ClientAdminHomePage = lazy(() => import("./pages/ClientAdmin"));
const SuperUserHomePage = lazy(() => import("./pages/SuperUser"));
const SalesHomePage = lazy(() => import("./pages/Sales"));

// Lazy Routes
const DashboardPage = lazy(() => import('./pages/Shooter/DashboardPage'));
const ShooterProfile = lazy(() => import('./pages/Shooter/ShooterProfilePage'));

const ClientAdminUsage = lazy(() => import("./pages/ClientAdmin/./UsagePage"));
const ClientAdminAnalytics = lazy(() => import("./pages/ClientAdmin/./AnalyticsPage"));
const ClientAdminSessions = lazy(() => import("./pages/ClientAdmin/./SessionsPage"));

const Profile = lazy(() => import("./pages/Shared/ProfilePage"));
const AccountRootsPage = lazy(() => import("./pages/Shared/AccountRootsPage"));
const ManagedUsersPage = lazy(() => import("./pages/Shared/ManagedUsersPage"));
const VCatKitsPage = lazy(() => import("./pages/Shared/VcatKitsPage"));
const ActiveSessionsPage = lazy(() => import("./pages/SuperUser/ActiveSessionsPage"));
const CatalogPage = lazy(() => import('./pages/SuperUser/CatalogPage'));
const PackagesPage = lazy(() => import('./pages/SuperUser/PackagesPage'));

const BillingPage = lazy(() => import("./pages/Sales/BillingPage"));

const ShootingDataPage = lazy(() => import("./pages/Shared/ShootingDataPage"));
const ScenariosPage = lazy(() => import('./features/ShootingData/Scenarios'));
const RepetitionsPage = lazy(() => import('./features/ShootingData/Repetitions'));
const DrillPage = lazy(() => import('./features/ShootingData/Drills'));

type WrapperProps = { children: React.ReactNode };

const Wrapper = ({ children }: WrapperProps): React.ReactElement => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return <>{children}</>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      refetchOnReconnect: true,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Helmet>
          <title>VCAT</title>
        </Helmet>
        <GlobalStyles
          styles={{
            '.auth-page input:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px #0E1720 inset',
              WebkitTextFillColor: '#FFFFFF',
              transition: 'background-color 9999s ease-in-out 0s',
            },
            '.auth-page input:-webkit-autofill:focus': {
              WebkitBoxShadow: '0 0 0 1000px #0E1720 inset',
            },
          }}
        />

        <Wrapper>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />

            <Route
              path="/vcat-user"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.PERSONAL]}
                  allowedRoles={[null]}
                  allowedAccounts={[null]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<ShooterHomePage />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="user-info" element={<ShooterProfile />} />

                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="shooting-data" element={<ShootingDataPage />} />
                <Route path="shooting-data/:sessionId" element={<ScenariosPage />} />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionsPage />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillPage />}
                />
              </Route>
            </Route>

            <Route
              path="/client-admin"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.MANAGED]}
                  allowedAccounts={[ESuperUserAccountType.CLIENT, ESuperUserAccountType.EYEVIATION]}
                  allowedRoles={[Role.ADMIN, Role.SUPER_USER]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<ClientAdminHomePage />}>
                <Route index element={<Navigate to="/usage" />} />
                <Route path="usage" element={<ClientAdminUsage />} />
                <Route path="analytics" element={<ClientAdminAnalytics />} />
                <Route path="sessions" element={<ClientAdminSessions />} />
                <Route path="user-info" element={<Profile />} />

                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="shooting-data" element={<ShootingDataPage />} />
                <Route path="shooting-data/:sessionId" element={<ScenariosPage />} />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionsPage />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillPage />}
                />
              </Route>
            </Route>

            <Route
              path="/eyeviation-super"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.MANAGED]}
                  allowedAccounts={[ESuperUserAccountType.CLIENT, ESuperUserAccountType.EYEVIATION]}
                  allowedRoles={[Role.SUPER_USER]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<SuperUserHomePage />}>
                <Route
                  index
                  element={<Navigate to="/eyeviation-super/accounts" />}
                />
                <Route path="user-info" element={<Profile />} />
                <Route path="accounts" element={<AccountRootsPage />} />
                <Route path="managed-users" element={<ManagedUsersPage />} />
                <Route path="vcat-kits" element={<VCatKitsPage />} />
                <Route path="active-sessions" element={<ActiveSessionsPage />}
                />
                <Route path="catalog" element={<CatalogPage />}
                />
                <Route path="packages" element={<PackagesPage />}
                />

                <Route path="shooting-data" element={<ShootingDataPage />} />
                <Route path="shooting-data/:sessionId" element={<ScenariosPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionsPage />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillPage />}
                />
              </Route>
            </Route>

            <Route
              path="/sales"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.MANAGED]}
                  allowedAccounts={[ESalesAccountType.CLIENT, ESalesAccountType.EYEVIATION]}
                  allowedRoles={[Role.MODERATOR]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<SalesHomePage />}>
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="accounts" element={<AccountRootsPage />} />
                <Route path="user-info" element={<Profile />} />
                <Route path="vcat-kits" element={<VCatKitsPage />} />
                <Route path="managed-users" element={<ManagedUsersPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="packages" element={<PackagesPage />} />

                <Route path="shooting-data" element={<ShootingDataPage />} />
                <Route path="shooting-data/:sessionId" element={<ScenariosPage />} />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionsPage />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillPage />}
                />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Wrapper>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export { queryClient };
export default App;
